'use client'

import { Play } from 'lucide-react'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { containerClassName } from '@/components/Container'
import { ImageGalleryStripCarousel } from '@/components/Gallery/ImageGalleryStripCarousel'
import type { ImageGalleryStripSlide } from '@/components/Gallery/imageGalleryStrip'
import { VideoModal } from '@/components/VideoModal'
import { useMessages } from '@/lib/i18n/useMessages'
import type { City } from '@/lib/mock/cities'
import { cn } from '@/utilities/ui'

type CityJourneySectionProps = {
  city: City
  gallerySlides?: ImageGalleryStripSlide[]
}

export const CityJourneySection: React.FC<CityJourneySectionProps> = ({
  city,
  gallerySlides,
}) => {
  const { journey } = city
  const messages = useMessages()
  const journeyTitle = messages.cityPage.journeyTitle
  const [videoOpen, setVideoOpen] = useState(false)
  const playButtonRef = useRef<HTMLButtonElement>(null)
  const hasVideo = Boolean(journey.videoUrl)

  const handleCloseVideo = useCallback(() => {
    setVideoOpen(false)
    playButtonRef.current?.focus()
  }, [])

  const stripSlides = useMemo(() => {
    if (gallerySlides && gallerySlides.length > 0) {
      return gallerySlides
    }

    return journey.images.map((item) => ({
      id: item.id,
      image: item.src,
      imageAlt: item.alt,
    }))
  }, [gallerySlides, journey.images])

  return (
    <section aria-labelledby="city-journey-heading" className="isolate overflow-x-clip bg-bluetext">
      <div className="relative min-h-[28rem] overflow-hidden pb-0 lg:min-h-[45rem]">
        <img
          alt={journey.backgroundImageAlt}
          aria-hidden={!journey.backgroundImageAlt}
          className="absolute inset-0 size-full object-cover object-center"
          src={journey.backgroundImage}
        />
        <div aria-hidden className="absolute inset-0 bg-bluetext/75" />

        <div className={cn(containerClassName, 'relative z-10 py-12 lg:py-16')}>
          <div className="mx-auto flex max-w-xl flex-col items-center gap-10 text-center mb-12 lg:mb-0">
            <img
              alt={journey.decorativeImageAlt}
              aria-hidden={!journey.decorativeImageAlt}
              className="pointer-events-none h-auto w-28 -scale-x-100 lg:w-36 xl:w-52"
              src={journey.decorativeImage}
            />
            <h2
              className="text-balance font-display text-[2.5rem] font-semibold leading-[1.05] text-white md:text-[3.5rem] lg:text-[4rem]"
              id="city-journey-heading"
            >
              <span className="block">
                <span className="text-turquoise">{journeyTitle}</span>
              </span>
              <span className="block">{city.name}</span>
            </h2>

            {hasVideo ? (
              <button
                ref={playButtonRef}
                aria-label={journey.playAriaLabel}
                className="inline-flex flex-col items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                onClick={() => setVideoOpen(true)}
                type="button"
              >
                <span className="inline-flex size-20 items-center justify-center rounded-full bg-turquoise shadow-lg md:size-24">
                  <Play
                    aria-hidden
                    className="ml-1 size-8 fill-bluetext text-bluetext md:size-10"
                  />
                </span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <ImageGalleryStripCarousel
        ariaLabel={journey.galleryAriaLabel}
        bgColor="bg-cream"
        carouselNextLabel={journey.galleryScrollNext}
        carouselPreviousLabel={journey.galleryScrollPrevious}
        className="pb-px"
        slides={stripSlides}
      />

      {hasVideo && journey.videoUrl ? (
        <VideoModal
          closeLabel={journey.videoModalCloseLabel}
          onClose={handleCloseVideo}
          open={videoOpen}
          title={journey.videoModalTitle}
          videoMimeType={journey.videoMimeType}
          videoUrl={journey.videoUrl}
        />
      ) : null}
    </section>
  )
}
